/**
 * MealPlan Store
 *
 * Manages meal plan state using Zustand with persistence.
 * Provides CRUD operations for meal plans and meal slots, including
 * recipe assignment, portion adjustment, and week navigation.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  MealPlan,
  MealSlot,
  MealPlanFormData,
  MealSlotUpdateData,
  DayOfWeek,
  MealType,
} from '../types/mealPlan';
import api from '../services/api';

interface MealPlanState {
  mealPlans: MealPlan[];
  currentMealPlan: MealPlan | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchMealPlans: () => Promise<void>;
  fetchMealPlan: (id: number) => Promise<void>;
  createMealPlan: (data: MealPlanFormData) => Promise<MealPlan>;
  updateMealPlan: (id: number, data: MealPlanFormData) => Promise<MealPlan>;
  deleteMealPlan: (id: number) => Promise<void>;
  setCurrentMealPlan: (mealPlan: MealPlan | null) => void;

  // Meal Slot Actions
  updateMealSlot: (
    mealPlanId: number,
    slotId: number,
    data: MealSlotUpdateData
  ) => Promise<MealSlot>;
  assignRecipeToSlot: (
    mealPlanId: number,
    day: DayOfWeek,
    mealType: MealType,
    recipeId: number,
    servings: number
  ) => Promise<MealSlot>;
  removeMealSlot: (mealPlanId: number, slotId: number) => Promise<void>;

  // Navigation
  navigateWeek: (direction: 'prev' | 'next') => Promise<void>;
  getCurrentWeekPlan: () => Promise<void>;

  // Utilities
  clearError: () => void;
}

/**
 * MealPlan Store with Zustand
 *
 * Uses persist middleware to cache meal plans for offline access.
 * Provides CRUD operations for meal plans and meal slots with error handling.
 */
export const useMealPlanStore = create<MealPlanState>()(
  persist(
    (set, get) => ({
      mealPlans: [],
      currentMealPlan: null,
      isLoading: false,
      error: null,

      fetchMealPlans: async () => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.get('/meal_plans');

          set({
            mealPlans: response.data.meal_plans || response.data.data || response.data,
            isLoading: false,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch meal plans',
          });
          throw error;
        }
      },

      fetchMealPlan: async (id: number) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.get(`/meal_plans/${id}`);

          set({
            currentMealPlan: response.data.meal_plan || response.data.data || response.data,
            isLoading: false,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch meal plan',
          });
          throw error;
        }
      },

      createMealPlan: async (data: MealPlanFormData) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post('/meal_plans', { meal_plan: data });
          const newMealPlan = response.data.meal_plan || response.data.data || response.data;

          set((state) => ({
            mealPlans: [...state.mealPlans, newMealPlan],
            currentMealPlan: newMealPlan,
            isLoading: false,
          }));

          return newMealPlan;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to create meal plan',
          });
          throw error;
        }
      },

      updateMealPlan: async (id: number, data: MealPlanFormData) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.put(`/meal_plans/${id}`, { meal_plan: data });
          const updatedMealPlan = response.data.meal_plan || response.data.data || response.data;

          set((state) => ({
            mealPlans: state.mealPlans.map((mealPlan) =>
              mealPlan.id === id ? updatedMealPlan : mealPlan
            ),
            currentMealPlan: state.currentMealPlan?.id === id ? updatedMealPlan : state.currentMealPlan,
            isLoading: false,
          }));

          return updatedMealPlan;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to update meal plan',
          });
          throw error;
        }
      },

      deleteMealPlan: async (id: number) => {
        set({ isLoading: true, error: null });

        try {
          await api.delete(`/meal_plans/${id}`);

          set((state) => ({
            mealPlans: state.mealPlans.filter((mealPlan) => mealPlan.id !== id),
            currentMealPlan: state.currentMealPlan?.id === id ? null : state.currentMealPlan,
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to delete meal plan',
          });
          throw error;
        }
      },

      setCurrentMealPlan: (mealPlan: MealPlan | null) => {
        set({ currentMealPlan: mealPlan });
      },

      updateMealSlot: async (
        mealPlanId: number,
        slotId: number,
        data: MealSlotUpdateData
      ) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.put(
            `/meal_plans/${mealPlanId}/meal_slots/${slotId}`,
            { meal_slot: data }
          );
          const updatedSlot = response.data.meal_slot || response.data.data || response.data;

          // Update the meal slot in the current meal plan
          set((state) => {
            if (!state.currentMealPlan || state.currentMealPlan.id !== mealPlanId) {
              return { isLoading: false };
            }

            const updatedMealSlots = state.currentMealPlan.meal_slots?.map((slot) =>
              slot.id === slotId ? updatedSlot : slot
            );

            return {
              currentMealPlan: {
                ...state.currentMealPlan,
                meal_slots: updatedMealSlots,
              },
              isLoading: false,
            };
          });

          return updatedSlot;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to update meal slot',
          });
          throw error;
        }
      },

      assignRecipeToSlot: async (
        mealPlanId: number,
        day: DayOfWeek,
        mealType: MealType,
        recipeId: number,
        servings: number
      ) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post(
            `/meal_plans/${mealPlanId}/meal_slots`,
            {
              meal_slot: {
                day,
                meal_type: mealType,
                recipe_id: recipeId,
                servings,
              },
            }
          );
          const newSlot = response.data.meal_slot || response.data.data || response.data;

          // Add the new slot to the current meal plan
          set((state) => {
            if (!state.currentMealPlan || state.currentMealPlan.id !== mealPlanId) {
              return { isLoading: false };
            }

            const updatedMealSlots = [...(state.currentMealPlan.meal_slots || []), newSlot];

            return {
              currentMealPlan: {
                ...state.currentMealPlan,
                meal_slots: updatedMealSlots,
              },
              isLoading: false,
            };
          });

          return newSlot;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to assign recipe to slot',
          });
          throw error;
        }
      },

      removeMealSlot: async (mealPlanId: number, slotId: number) => {
        set({ isLoading: true, error: null });

        try {
          await api.delete(`/meal_plans/${mealPlanId}/meal_slots/${slotId}`);

          // Remove the slot from the current meal plan
          set((state) => {
            if (!state.currentMealPlan || state.currentMealPlan.id !== mealPlanId) {
              return { isLoading: false };
            }

            const updatedMealSlots = state.currentMealPlan.meal_slots?.filter(
              (slot) => slot.id !== slotId
            );

            return {
              currentMealPlan: {
                ...state.currentMealPlan,
                meal_slots: updatedMealSlots,
              },
              isLoading: false,
            };
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to remove meal slot',
          });
          throw error;
        }
      },

      navigateWeek: async (direction: 'prev' | 'next') => {
        const { currentMealPlan } = get();
        if (!currentMealPlan) return;

        const currentStartDate = new Date(currentMealPlan.start_date);
        const daysToAdd = direction === 'next' ? 7 : -7;

        const newStartDate = new Date(currentStartDate);
        newStartDate.setDate(currentStartDate.getDate() + daysToAdd);

        const newEndDate = new Date(newStartDate);
        newEndDate.setDate(newStartDate.getDate() + 6);

        set({ isLoading: true, error: null });

        try {
          // Try to find existing meal plan for this week
          const response = await api.get('/meal_plans', {
            params: {
              start_date: newStartDate.toISOString().split('T')[0],
            },
          });

          const plans = response.data.meal_plans || response.data.data || response.data;
          const existingPlan = Array.isArray(plans) ? plans[0] : null;

          if (existingPlan) {
            // Load existing meal plan
            const detailResponse = await api.get(`/meal_plans/${existingPlan.id}`);
            set({
              currentMealPlan: detailResponse.data.meal_plan || detailResponse.data.data || detailResponse.data,
              isLoading: false,
            });
          } else {
            // Create new meal plan for this week
            const newMealPlan = await get().createMealPlan({
              start_date: newStartDate.toISOString().split('T')[0],
              end_date: newEndDate.toISOString().split('T')[0],
            });
            set({ currentMealPlan: newMealPlan, isLoading: false });
          }
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to navigate week',
          });
          throw error;
        }
      },

      getCurrentWeekPlan: async () => {
        set({ isLoading: true, error: null });

        try {
          // Calculate current week's start (Monday) and end (Sunday)
          const now = new Date();
          const dayOfWeek = now.getDay();
          const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

          const startDate = new Date(now);
          startDate.setDate(now.getDate() + mondayOffset);

          const endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6);

          // Try to find existing meal plan for current week
          const response = await api.get('/meal_plans', {
            params: {
              start_date: startDate.toISOString().split('T')[0],
            },
          });

          const plans = response.data.meal_plans || response.data.data || response.data;
          const existingPlan = Array.isArray(plans) ? plans[0] : null;

          if (existingPlan) {
            // Load existing meal plan
            const detailResponse = await api.get(`/meal_plans/${existingPlan.id}`);
            set({
              currentMealPlan: detailResponse.data.meal_plan || detailResponse.data.data || detailResponse.data,
              isLoading: false,
            });
          } else {
            // Create new meal plan for current week
            const newMealPlan = await get().createMealPlan({
              start_date: startDate.toISOString().split('T')[0],
              end_date: endDate.toISOString().split('T')[0],
            });
            set({ currentMealPlan: newMealPlan, isLoading: false });
          }
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to get current week plan',
          });
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'meal-plan-storage', // localStorage key
      version: 2, // Bump to invalidate stale cached data from old format
      partialize: (state) => ({
        // Persist meal plans and currentMealPlan for offline access
        // Don't persist loading/error states
        mealPlans: state.mealPlans,
        currentMealPlan: state.currentMealPlan,
      }),
    }
  )
);
